import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";
import { faArrowAltCircleDown } from "@fortawesome/free-solid-svg-icons/faArrowAltCircleDown";
import { Trans, useTranslation } from "react-i18next";
import { AI_PROVIDER_MODELS, providerLabelByModel } from "../chatbot.utils";
import { AtiraDropdown } from "../../../../../components/AtiraDropdown";
import { TestIDs } from "../../../../../tests/types/testIds";
import { Spacing } from "../../../../../theme/Spacing";
import styled from "styled-components";
import { Button } from "../../../../../components/Button";
import { Textarea } from "../../../../../components/Textarea";
import { FormType } from "../../../../../model/form/types/FormType.enum";
import { formSliceSelectors } from "../../../../../redux/forms/form.selector";
import { useAppSelector } from "../../../../../redux/store";
import { AtiraSelect } from "../../../../../components/AtiraSelect";
import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { faFileSignature } from "@fortawesome/free-solid-svg-icons/faFileSignature";
import { useStepperRegisterValidation } from "../../../../../components/stepper/useStepperRegisterValidation";
import { useCallback } from "react";
import { useStepContext } from "../../../../../components/stepper/StepperContext";
import { CreateChatbotDto } from "../../../../../model/chatbot/dto/CreateChatbotDto";
import { UpdateChatbotDto } from "../../../../../model/chatbot/dto/UpdateChatbotDto";
import { ChatbotAssistantKind } from "../../../../../model/chatbot/types/Chatbot.assistant-kind.enum";
import { Clickable } from "../../../../../components/Clickable";
import { MenuProps, Switch, Tooltip } from "../../../../../utils/local-antd";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { Rounded } from "../../../../../theme/Rounded";
import { faPerson } from "@fortawesome/free-solid-svg-icons/faPerson";
import { EntityIcons } from "../../../../../utils/GlobalIcons";
import { Info } from "../../../../../components/Info";

const Container = styled(Flex)`
  gap: ${Spacing.xl};
  width: 50%;
  flex-wrap: wrap;
  align-self: flex-start;
  flex-direction: column;
`;

const StyledButton = styled(Button)<{ $valid: boolean }>`
  width: fit-content;
  padding: ${Spacing.s};
  background-color: white;
  color: ${({ theme }) => theme.main};
  text-align: start;
  border: 1px solid
    ${({ theme, $valid }) => ($valid ? theme.main : theme.danger)};

  &:hover {
    background-color: ${({ theme }) => theme.main};
    color: white;
  }
`;

const StyledInfo = styled(Tooltip)`
  cursor: help;
`;

const StyledClickable = styled(Clickable)<{ selected: boolean }>`
  border: 1px solid ${({ theme }) => theme.sub};
  border-radius: ${Rounded.md};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.l};
  outline: ${({ selected, theme }) =>
    selected ? `2px solid ${theme.main}` : `1px solid ${theme.sub}`};
  padding: ${Spacing.l};
  width: 25rem;
  box-shadow: ${({ selected, theme }) =>
    selected ? `0 0 10px ${theme.main}33` : "none"};
  box-sizing: border-box;
  opacity: ${({ selected }) => (selected ? 1 : 0.6)};
  height: 18rem;

  &:hover {
    border-color: ${({ theme }) => theme.main};
  }
`;

const CustomText = styled(Text)<{ $index: number }>`
  display: inline-block;
  color: ${({ theme }) => theme.white};
  padding: ${Spacing.xs};
  border-radius: ${Rounded.md};
  background-color: ${({ $index, theme }) =>
    $index === 0 ? theme.danger : $index === 1 ? theme.warning : theme.success};
  width: fit-content;
`;

export const SettingsAIChatBotStepEssentials = () => {
  const { control, watch } = useFormContext<
    CreateChatbotDto | UpdateChatbotDto
  >();
  const { t } = useTranslation();
  const { currentStep } = useStepContext();

  const [aiModel, aiKey, formId] = watch([
    "aiConfig.aiModel",
    "aiConfig.aiKey",
    "formId",
  ]);

  const canGoNext = useCallback(() => {
    return Boolean(aiModel && aiKey && formId);
  }, [aiModel, aiKey, formId]);

  useStepperRegisterValidation(currentStep, canGoNext);

  const entriesForms =
    useAppSelector(formSliceSelectors.selectForms)?.filter(
      (f) => f.type === FormType.ENTRY && f.orphan !== true,
    ) || [];

  const canEnableChatBot = true;
  const shortKey = useWatch({ control, name: "aiConfig.aiKey" });
  const formOptions = entriesForms.map((f) => ({
    label: f.title,
    value: f._id,
    "data-testid": TestIDs.chatbot.drawer.createEdit.form.option(f._id),
  }));

  const modelOptions = (
    onSelectModel: (model: string) => void,
  ): MenuProps["items"] =>
    AI_PROVIDER_MODELS.map((provider) => ({
      key: `provider-${provider.providerKey}`,
      label: t(provider.providerLabel),
      "data-testid": TestIDs.chatbot.drawer.createEdit.model.provider?.(
        provider.providerKey,
      ),
      children: provider.models.map((m, i) => ({
        key: `model-${provider.providerKey}-${m.value}`,
        label: m.label ? (
          <Trans
            i18nKey={m.label}
            components={[<CustomText $index={i} fontSize="xs" />]}
          >
            {m.value}
          </Trans>
        ) : (
          m.value
        ),
        "data-testid": TestIDs.chatbot.drawer.createEdit.model.option?.(
          m.value,
        ),
        onClick: () => onSelectModel(m.value),
      })),
    }));

  return (
    <Container>
      <Flex alignItems="center" gap="s">
        <AtiraIcon icon={faFileSignature} color="main" size="xl" />

        <Text fontSize="l" fontWeight={"bold"} color="main">
          {t("settings.aiChatbot.model.model")}
        </Text>
      </Flex>

      <Controller
        name="aiConfig.aiModel"
        control={control}
        rules={{
          required: {
            value: true,
            message: t("common.this_field_required"),
          },
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          const providerLabel = value ? providerLabelByModel.get(value) : null;
          const buttonLabel = value
            ? providerLabel
              ? `${providerLabel} / ${value}`
              : value
            : t("common.select");

          return (
            <Flex flexDirection="column">
              <Text>{t("settings.aiChatbot.model.modelTitle")}</Text>

              <AtiraDropdown
                items={modelOptions(onChange)}
                placement="bottomLeft"
                trigger={["click"]}
                disabled={!canEnableChatBot}
                arrow={{ pointAtCenter: true }}
                destroyOnHidden
              >
                <StyledButton
                  hover
                  icon={faArrowAltCircleDown}
                  $valid={!error?.message}
                  className={TestIDs.chatbot.drawer.createEdit.model.open}
                >
                  {buttonLabel}
                </StyledButton>
              </AtiraDropdown>
              {error?.message ? (
                <Text color="danger" fontSize="s">
                  {error.message}
                </Text>
              ) : null}
            </Flex>
          );
        }}
      />

      <Controller
        name="aiConfig.aiAssistantKind"
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <Flex flexDirection="column" gap="s">
              <Flex
                alignItems="flex-start"
                flexDirection="column"
                justifyContent="center"
                gap="s"
              >
                <Flex gap="s">
                  <StyledInfo
                    title={t("settings.aiChatbot.assistantKind.info")}
                  >
                    <span>
                      <AtiraIcon icon={faInfoCircle} color="main" size="sm" />
                    </span>
                  </StyledInfo>
                  <Text>{t("settings.aiChatbot.assistantKind.title")}</Text>
                </Flex>
                <Text color="darkerSub">
                  {t("settings.aiChatbot.assistantKind.description")}
                </Text>
              </Flex>

              <Flex alignItems="center" gap="m">
                {Object.values(ChatbotAssistantKind).map((kind) => (
                  <Clickable
                    key={kind}
                    onClick={() => onChange(kind)}
                    dataTestId={TestIDs.chatbot.drawer.createEdit.fields.assistantKind(
                      kind,
                    )}
                  >
                    <StyledClickable selected={value === kind}>
                      <Flex alignItems="center" gap="s">
                        <AtiraIcon
                          icon={
                            kind === ChatbotAssistantKind.ASSISTANT
                              ? faPerson
                              : EntityIcons.Forms
                          }
                          color="main"
                          size="xl"
                        />
                        <Text fontSize="m" fontWeight="bold" color="main">
                          {t(
                            `settings.aiChatbot.assistantKind.kind.${kind.toLowerCase()}`,
                          )}
                        </Text>
                      </Flex>
                      <Text fontSize="s" color="darkerSub">
                        {t(
                          `settings.aiChatbot.assistantKind.kind.${kind.toLowerCase()}.description`,
                        )}
                      </Text>
                    </StyledClickable>
                  </Clickable>
                ))}
              </Flex>
            </Flex>
          );
        }}
      />

      <Controller
        name="aiConfig.aiKey"
        control={control}
        rules={{
          required: {
            value: true,
            message: t("common.this_field_required"),
          },
        }}
        render={({ fieldState: { error }, field: { onChange, value } }) => (
          <Textarea
            value={value}
            onChange={onChange}
            placeholder={
              shortKey || t("settings.aiChatbot.key.inputPlaceholder")
            }
            title={t("settings.aiChatbot.key.inputTitle")}
            info={t("settings.aiChatbot.key.inputInfo")}
            valid={!error?.message}
            errorMessage={error?.message}
            required
            dataTestId={TestIDs.chatbot.drawer.createEdit.fields.apiKey}
          />
        )}
      />

      <Controller
        name="formId"
        control={control}
        rules={{
          required: {
            value: true,
            message: t("common.this_field_required"),
          },
        }}
        render={({ fieldState: { error }, field: { onChange, value } }) => (
          <AtiraSelect
            title={t("settings.aiChatbot.form.title")}
            info={t("settings.aiChatbot.form.info")}
            options={formOptions}
            value={formOptions?.find((f) => f.value === value)}
            onChange={onChange}
            required
            valid={!error?.message}
            errorMessage={error?.message}
            className={TestIDs.chatbot.drawer.createEdit.form.open}
          />
        )}
      />

      <Controller
        name="config.requireFormFillingBeforeChatting"
        control={control}
        rules={{
          required: {
            value: true,
            message: t("common.this_field_required"),
          },
        }}
        render={({ field: { onChange, value } }) => (
          <Flex flexDirection="column" gap="s">
            <Flex alignItems="center" gap="s">
              <Switch
                onChange={onChange}
                checked={value}
                className={
                  TestIDs.chatbot.drawer.createEdit.fields
                    .requireFormFillingBeforeChatting
                }
              />
              <Text>
                {t(
                  "settings.aiChatbot.config.requireFormFillingBeforeChatting.title",
                )}
              </Text>
            </Flex>
            <Info
              info={t(
                "settings.aiChatbot.config.requireFormFillingBeforeChatting.info",
              )}
            />
          </Flex>
        )}
      />
    </Container>
  );
};
